// ─── APA Table Renderer ────────────────────────────────────────────────────────
// Generates publication-quality APA 7th edition tables as HTML strings.
// Rules: no vertical lines, ruled top/bottom borders, horizontal header rule,
// right-aligned numbers, left-aligned labels, significance stars in footnote.

const STYLE = `
  .apa-table-wrap { font-family: "Times New Roman", Georgia, serif; font-size: 12pt; color: #000; margin: 1.5rem 0; }
  .apa-table-title { font-style: italic; font-size: 12pt; margin-bottom: 0.25rem; }
  .apa-table { width: 100%; border-collapse: collapse; border-top: 2px solid #000; border-bottom: 2px solid #000; }
  .apa-table thead tr { border-bottom: 1px solid #000; }
  .apa-table th { font-weight: normal; text-align: center; padding: 4px 10px; font-size: 11pt; }
  .apa-table th.left { text-align: left; }
  .apa-table td { padding: 3px 10px; font-size: 11pt; vertical-align: top; }
  .apa-table td.num { text-align: right; }
  .apa-table td.ctr { text-align: center; }
  .apa-table .sig { font-size: 10pt; vertical-align: super; }
  .apa-table .note { font-size: 10pt; margin-top: 4px; }
  .apa-table tbody tr:last-child td { padding-bottom: 4px; }
`

function wrap(tableNum, title, tableHtml, noteHtml) {
  return `<div class="apa-table-wrap">
<style>${STYLE}</style>
<div class="apa-table-title">Table ${tableNum}</div>
<div style="font-style:italic;font-size:12pt;margin-bottom:6px">${title}</div>
${tableHtml}
${noteHtml ? `<div class="note"><em>Note.</em> ${noteHtml}</div>` : ''}
</div>`
}

function sigStars(p) {
  if (p == null) return ''
  if (p < 0.001) return '<span class="sig">***</span>'
  if (p < 0.01)  return '<span class="sig">**</span>'
  if (p < 0.05)  return '<span class="sig">*</span>'
  if (p < 0.10)  return '<span class="sig">†</span>'
  return ''
}

function fmt(v, dec = 2) {
  if (v == null || !isFinite(v)) return '—'
  const n = Number(v.toFixed(dec))
  // APA convention: omit leading zero for values bounded by ±1 (r, p, β < 1)
  if (Math.abs(n) < 1 && n !== 0) {
    return (n < 0 ? '-' : '') + Math.abs(n).toFixed(dec).slice(1)
  }
  return n.toString()
}

// ── OLS Regression Table ──────────────────────────────────────────────────────

/**
 * Generate APA 7th edition OLS regression results table.
 */
export function renderOLSTable(olsResult, tableNum = 1) {
  if (!olsResult) return ''
  const coefs = Object.entries(olsResult.coefficients).filter(([k]) => k !== 'const')
  const intercept = olsResult.coefficients['const']

  const header = `<thead><tr>
    <th class="left" style="width:28%">Variable</th>
    <th>B</th><th>SE</th><th>β</th><th>t</th><th>p</th><th>95% CI</th><th>VIF</th>
  </tr></thead>`

  const rows = coefs.map(([name, c]) => `<tr>
    <td>${name.replace(/_/g, ' ')}</td>
    <td class="num">${fmt(c.beta, 3)}</td>
    <td class="num">${fmt(c.std_err, 3)}</td>
    <td class="num">${fmt(c.beta, 3)}</td>
    <td class="num">${fmt(c.t_stat, 2)}</td>
    <td class="num">${fmt(c.p_value, 3)}${sigStars(c.p_value)}</td>
    <td class="num">[${fmt(c.ci_lower, 3)}, ${fmt(c.ci_upper, 3)}]</td>
    <td class="num">${olsResult.vif?.[name] != null ? fmt(olsResult.vif[name], 2) : '—'}</td>
  </tr>`).join('')

  const modelRows = `<tr style="border-top:1px solid #000">
    <td colspan="2"><em>R²</em> = ${fmt(olsResult.r_squared, 3)}</td>
    <td colspan="2"><em>Adj. R²</em> = ${fmt(olsResult.adj_r_squared, 3)}</td>
    <td colspan="2"><em>F</em>(${olsResult.ivs?.length}, ${olsResult.n - olsResult.ivs?.length - 1}) = ${fmt(olsResult.f_statistic, 2)}</td>
    <td colspan="2"><em>p</em> = ${fmt(olsResult.f_pvalue, 4)}${sigStars(olsResult.f_pvalue)}</td>
  </tr>`

  const table = `<table class="apa-table">
    ${header}
    <tbody>${rows}${modelRows}</tbody>
  </table>`

  const note = `<em>N</em> = ${olsResult.n}. B = unstandardised coefficient. 95% CI = 95% confidence interval for B. VIF = variance inflation factor. † <em>p</em> &lt; .10. * <em>p</em> &lt; .05. ** <em>p</em> &lt; .01. *** <em>p</em> &lt; .001.`

  return wrap(tableNum, `OLS Regression Results: ${olsResult.dv} as Dependent Variable`, table, note)
}

// ── Descriptive Statistics Table ──────────────────────────────────────────────

export function renderDescriptiveTable(descResult, tableNum = 1) {
  if (!descResult) return ''
  const vars = Object.entries(descResult)

  const header = `<thead><tr>
    <th class="left">Variable</th>
    <th><em>n</em></th><th><em>M</em></th><th><em>SD</em></th>
    <th>Min</th><th>Mdn</th><th>Max</th><th>Skew</th><th>Kurt.</th>
  </tr></thead>`

  const rows = vars.map(([name, s]) => `<tr>
    <td>${(s.label || name).replace(/_/g, ' ')}</td>
    <td class="num">${s.n}</td>
    <td class="num">${fmt(s.mean, 2)}</td>
    <td class="num">${fmt(s.std, 2)}</td>
    <td class="num">${fmt(s.min, 2)}</td>
    <td class="num">${fmt(s.median, 2)}</td>
    <td class="num">${fmt(s.max, 2)}</td>
    <td class="num">${fmt(s.skewness, 2)}</td>
    <td class="num">${fmt(s.kurtosis, 2)}</td>
  </tr>`).join('')

  const table = `<table class="apa-table">${header}<tbody>${rows}</tbody></table>`
  const note = `Skew. = skewness; Kurt. = excess kurtosis. Missing values excluded listwise per variable.`
  return wrap(tableNum, 'Descriptive Statistics for Study Variables', table, note)
}

// ── Correlation Matrix Table ──────────────────────────────────────────────────

export function renderCorrelationTable(corrResult, descResult, tableNum = 1) {
  if (!corrResult) return ''
  const vars = corrResult.variables

  const header = `<thead><tr>
    <th class="left">Variable</th>
    <th><em>M</em></th><th><em>SD</em></th>
    ${vars.map((_, i) => `<th>${i + 1}</th>`).join('')}
  </tr></thead>`

  const rows = vars.map((v, i) => {
    const stats = descResult?.[v]
    return `<tr>
      <td>${i + 1}. ${v.replace(/_/g, ' ')}</td>
      <td class="num">${stats ? fmt(stats.mean, 2) : '—'}</td>
      <td class="num">${stats ? fmt(stats.std, 2) : '—'}</td>
      ${vars.map((v2, j) => {
        if (j > i) {
          const r = corrResult.matrix[v]?.[v2]
          const p = corrResult.pvalues[v]?.[v2]
          return `<td class="num">${r != null ? fmt(r, 2) : '—'}${sigStars(p)}</td>`
        }
        return `<td class="ctr">—</td>`
      }).join('')}
    </tr>`
  }).join('')

  const table = `<table class="apa-table">${header}<tbody>${rows}</tbody></table>`
  const note = `† <em>p</em> &lt; .10. * <em>p</em> &lt; .05. ** <em>p</em> &lt; .01. *** <em>p</em> &lt; .001 (two-tailed). Correlations below the diagonal shown; diagonal omitted.`
  return wrap(tableNum, 'Means, Standard Deviations, and Correlations Among Study Variables', table, note)
}

// ── ANOVA Table ───────────────────────────────────────────────────────────────

export function renderANOVATable(anovaResult, tableNum = 1) {
  if (!anovaResult) return ''

  const header = `<thead><tr>
    <th class="left">Source</th><th>SS</th><th>df</th><th>MS</th><th>F</th><th>p</th><th>η²</th>
  </tr></thead>`

  const rows = `
    <tr><td>Between groups</td><td class="num">${fmt(anovaResult.ss_between, 2)}</td><td class="num">${anovaResult.df_between}</td><td class="num">${fmt(anovaResult.ms_between, 2)}</td><td class="num">${fmt(anovaResult.f_statistic, 2)}</td><td class="num">${fmt(anovaResult.f_pvalue, 3)}${sigStars(anovaResult.f_pvalue)}</td><td class="num" rowspan="2">${fmt(anovaResult.eta_squared, 3)}</td></tr>
    <tr><td>Within groups</td><td class="num">${fmt(anovaResult.ss_within, 2)}</td><td class="num">${anovaResult.df_within}</td><td class="num">${fmt(anovaResult.ms_within, 2)}</td><td class="ctr">—</td><td class="ctr">—</td></tr>
  `

  const groupRows = Object.entries(anovaResult.group_stats || {}).map(([g, s]) =>
    `<tr><td style="padding-left:1.5rem">${g}</td><td class="num" colspan="2"><em>n</em> = ${s.n}</td><td class="num"><em>M</em> = ${fmt(s.mean, 2)}</td><td class="num" colspan="2"><em>SD</em> = ${fmt(s.std, 2)}</td><td class="ctr">—</td></tr>`
  ).join('')

  const table = `<table class="apa-table">${header}<tbody>${rows}<tr style="border-top:1px solid #000"><td colspan="7"><em>Group statistics</em></td></tr>${groupRows}</tbody></table>`
  const note = `η² = eta-squared. * <em>p</em> &lt; .05. ** <em>p</em> &lt; .01. *** <em>p</em> &lt; .001.`
  return wrap(tableNum, `One-Way ANOVA: ${anovaResult.dv} by ${anovaResult.group}`, table, note)
}

// ── Cronbach Alpha Table ──────────────────────────────────────────────────────

export function renderCronbachTable(cronbachResult, tableNum = 1) {
  if (!cronbachResult) return ''

  const header = `<thead><tr>
    <th class="left">Item</th><th>Item–Total r</th><th>α If Deleted</th>
  </tr></thead>`

  const rows = Object.entries(cronbachResult.item_total_correlations || {}).map(([item, r]) => `<tr>
    <td>${item.replace(/_/g, ' ')}</td>
    <td class="num">${fmt(r, 3)}</td>
    <td class="num">${cronbachResult.alpha_if_item_deleted?.[item] != null ? fmt(cronbachResult.alpha_if_item_deleted[item], 3) : '—'}</td>
  </tr>`).join('')

  const footer = `<tr style="border-top:1px solid #000">
    <td colspan="3"><em>Overall</em>: α = <strong>${cronbachResult.alpha}</strong> (${cronbachResult.reliability}); <em>k</em> = ${cronbachResult.n_items}; <em>N</em> = ${cronbachResult.n_obs}</td>
  </tr>`

  const table = `<table class="apa-table">${header}<tbody>${rows}${footer}</tbody></table>`
  const note = `Item–Total r = corrected item-total correlation. α If Deleted = Cronbach's alpha if item is removed. Reliability benchmarks: α ≥ .90, excellent; α ≥ .80, good; α ≥ .70, acceptable (Nunnally, 1978).`
  return wrap(tableNum, `Internal Consistency Analysis: ${cronbachResult.items?.join(', ')}`, table, note)
}

// ── Hierarchical OLS Regression Table ────────────────────────────────────────
// For multi-model comparison: Model 1 (controls), Model 2 (+ IVs), etc.

/**
 * Render a hierarchical regression table comparing multiple OLS models.
 *
 * @param {Array<OLSResult>} models — array in entry order (Model 1, Model 2, ...)
 * @param {number} tableNum
 * @returns {string}
 */
export function renderHierarchicalRegressionTable(models, tableNum = 1) {
  if (!models || models.length === 0) return ''

  // Collect all predictors across models (except const)
  const allPredictors = []
  const seen = new Set()
  for (const m of models) {
    for (const name of Object.keys(m.coefficients || {})) {
      if (name !== 'const' && !seen.has(name)) {
        allPredictors.push(name)
        seen.add(name)
      }
    }
  }

  const modelHeaders = models.map((_, i) => `<th colspan="2">Model ${i + 1}</th>`).join('')
  const subHeaders = models.map(() => `<th>β</th><th><em>p</em></th>`).join('')

  const header = `<thead>
    <tr>
      <th class="left" style="width:30%">Variable</th>
      ${modelHeaders}
    </tr>
    <tr style="border-bottom:1px solid #000">
      <th class="left"></th>
      ${subHeaders}
    </tr>
  </thead>`

  const rows = allPredictors.map(name => {
    const cells = models.map(m => {
      const c = m.coefficients?.[name]
      if (!c) return `<td class="ctr">—</td><td class="ctr">—</td>`
      return `<td class="num">${fmt(c.beta, 3)}${sigStars(c.p_value)}</td><td class="num">${fmt(c.p_value, 3)}</td>`
    }).join('')
    return `<tr><td>${name.replace(/_/g, ' ')}</td>${cells}</tr>`
  }).join('')

  // Model fit rows
  const fitRows = [
    ['<em>R²</em>', m => fmt(m.r_squared, 3)],
    ['Adj. <em>R²</em>', m => fmt(m.adj_r_squared, 3)],
    ['Δ<em>R²</em>', (m, i) => {
      if (i === 0) return '—'
      const prev = models[i - 1]
      const delta = (m.r_squared || 0) - (prev.r_squared || 0)
      return fmt(delta, 3)
    }],
    ['<em>F</em>', m => `${fmt(m.f_statistic, 2)}${sigStars(m.f_pvalue)}`],
    ['<em>N</em>', m => String(m.n)],
  ].map(([label, valFn]) => {
    const cells = models.map((m, i) => `<td class="num" colspan="2">${valFn(m, i)}</td>`).join('')
    return `<tr style="border-top:1px solid #000"><td><em>${label}</em></td>${cells}</tr>`
  }).join('')

  const table = `<table class="apa-table">${header}<tbody>${rows}${fitRows}</tbody></table>`
  const note = `β = standardised regression coefficient. Δ<em>R²</em> = change in <em>R²</em> relative to preceding model. † <em>p</em> &lt; .10. * <em>p</em> &lt; .05. ** <em>p</em> &lt; .01. *** <em>p</em> &lt; .001.`

  return wrap(tableNum, `Hierarchical OLS Regression Results (${models.length}-Model Comparison)`, table, note)
}

// ── Moderation Table ──────────────────────────────────────────────────────────

/**
 * Render moderation analysis table (interaction terms).
 *
 * @param {object} moderationResult — from statisticsEngine.computeModeration
 * @param {number} tableNum
 */
export function renderModeratorTable(moderationResult, tableNum = 1) {
  if (!moderationResult) return ''

  const models = [moderationResult.mainEffects, moderationResult.withInteraction].filter(Boolean)
  if (models.length < 2) return renderOLSTable(models[0], tableNum)

  const { ivName, moderatorName, interactionTerm, deltaR2 } = moderationResult

  const allPredictors = ['const', ivName, moderatorName, interactionTerm].filter(Boolean)

  const header = `<thead><tr>
    <th class="left" style="width:30%">Variable</th>
    <th colspan="2">Step 1 (Main Effects)</th>
    <th colspan="2">Step 2 (+ Interaction)</th>
  </tr><tr style="border-bottom:1px solid #000">
    <th class="left"></th><th>β</th><th><em>p</em></th><th>β</th><th><em>p</em></th>
  </tr></thead>`

  const rows = allPredictors.filter(p => p !== 'const').map(name => {
    const c1 = models[0]?.coefficients?.[name]
    const c2 = models[1]?.coefficients?.[name]
    const bold = name === interactionTerm
    const label = `${bold ? '<strong>' : ''}${name.replace(/_/g, ' ')}${bold ? '</strong>' : ''}`
    return `<tr${bold ? ' style="background:#f8fafc"' : ''}>
      <td>${label}</td>
      <td class="num">${c1 ? `${fmt(c1.beta, 3)}${sigStars(c1.p_value)}` : '—'}</td>
      <td class="num">${c1 ? fmt(c1.p_value, 3) : '—'}</td>
      <td class="num">${c2 ? `${fmt(c2.beta, 3)}${sigStars(c2.p_value)}` : '—'}</td>
      <td class="num">${c2 ? fmt(c2.p_value, 3) : '—'}</td>
    </tr>`
  }).join('')

  const fitRow = `<tr style="border-top:1px solid #000">
    <td><em>R²</em> (Adj. <em>R²</em>)</td>
    <td class="num" colspan="2">${fmt(models[0].r_squared, 3)} (${fmt(models[0].adj_r_squared, 3)})</td>
    <td class="num" colspan="2">${fmt(models[1].r_squared, 3)} (${fmt(models[1].adj_r_squared, 3)})</td>
  </tr>
  <tr>
    <td>Δ<em>R²</em></td>
    <td class="ctr" colspan="2">—</td>
    <td class="num" colspan="2">${fmt(deltaR2, 3)}${sigStars(moderationResult.interactionPValue)}</td>
  </tr>`

  const table = `<table class="apa-table">${header}<tbody>${rows}${fitRow}</tbody></table>`
  const note = `Interaction term (${interactionTerm?.replace(/_/g, ' ')}) is mean-centred. Δ<em>R²</em> reflects incremental variance explained by the interaction. † <em>p</em> &lt; .10. * <em>p</em> &lt; .05. ** <em>p</em> &lt; .01. *** <em>p</em> &lt; .001.`

  return wrap(tableNum, `Moderation Analysis: Effect of ${ivName?.replace(/_/g, ' ')} on ${models[0]?.dv?.replace(/_/g, ' ')}, Moderated by ${moderatorName?.replace(/_/g, ' ')}`, table, note)
}

// ── Summary Table ─────────────────────────────────────────────────────────────
// Compact hypothesis summary table suitable for Discussion chapter.

/**
 * Render a hypothesis outcome summary table.
 *
 * @param {Array<{ number, iv, dv, outcome, coef }>} hypothesisOutcomes
 * @param {number} tableNum
 */
export function renderHypothesisSummaryTable(hypothesisOutcomes, tableNum = 1) {
  if (!hypothesisOutcomes || hypothesisOutcomes.length === 0) return ''

  const header = `<thead><tr>
    <th class="left">Hypothesis</th>
    <th>Direction</th>
    <th>β</th>
    <th><em>p</em></th>
    <th>Outcome</th>
  </tr></thead>`

  const OUTCOME_LABELS = {
    supported:          'Supported',
    partially_supported:'Partially Supported',
    not_supported:      'Not Supported',
    not_testable:       'Not Testable',
  }

  const rows = hypothesisOutcomes.map(h => {
    const beta = h.coef?.beta != null ? fmt(h.coef.beta, 3) : '—'
    const pval = h.coef?.p_value != null ? `${fmt(h.coef.p_value, 3)}${sigStars(h.coef.p_value)}` : '—'
    const dir = h.hypothesis?.direction === 'negative' ? '−' : '+'
    const outcome = OUTCOME_LABELS[h.outcome] || h.outcome

    return `<tr>
      <td>H${h.number}: ${(h.iv || '').replace(/_/g, ' ')} → ${(h.dv || '').replace(/_/g, ' ')}</td>
      <td class="ctr">${dir}</td>
      <td class="num">${beta}</td>
      <td class="num">${pval}</td>
      <td class="ctr"><em>${outcome}</em></td>
    </tr>`
  }).join('')

  const supported = hypothesisOutcomes.filter(h => h.outcome === 'supported').length
  const total = hypothesisOutcomes.length

  const footer = `<tr style="border-top:1px solid #000">
    <td colspan="5"><em>Note.</em> ${supported} of ${total} hypotheses supported. β = standardised coefficient.</td>
  </tr>`

  const table = `<table class="apa-table">${header}<tbody>${rows}${footer}</tbody></table>`
  const note = `† <em>p</em> &lt; .10. * <em>p</em> &lt; .05. ** <em>p</em> &lt; .01. *** <em>p</em> &lt; .001. Partial support indicates significant coefficient in unexpected direction or marginal significance.`

  return wrap(tableNum, 'Summary of Hypothesis Testing Results', table, note)
}
